/**
 * Pure adapters from a persisted audience record (the oRPC `audience.get`/`audience.list` DTO shape)
 * to the view models the components render — `CountState`, staleness, rule counts, and the full
 * `AudienceListItem`. Kept out of the components so the list page and the artifact page derive the
 * four count states identically (the list showing a broken audience's failure is a product decision,
 * not incidental).
 */
import type { RouterClient } from '@orpc/server';
import { isCountFresh } from '@server/audience-count-freshness';
import { RS_PUSHED_SQL_SHAPE } from '@server/audience-member-id';
import type { AppRouter } from '@server/rpc';
import { isNeedsRepairPushError } from '@server/warehouse-repair';

import { type AudienceDefinition, type AudienceListItem, type CountState, topLevelConjuncts } from '../types';

/** The `audience.get` DTO, read off the router the real client is built from. Type-only, erased at
 *  build time — `_shared/lib/rpc.ts` imports `@server/rpc` the same way in production code. */
type AudienceDto = Awaited<ReturnType<RouterClient<AppRouter>['audience']['get']>>['audience'];

/**
 * The subset of the persisted audience row these adapters read — a genuine PROJECTION of the DTO
 * (`workspaceId`, `createdAt` and the raw `definition` string are not read here), which is the one case
 * where a local type earns its place. Derived with `Pick`, not re-declared: a field renamed on the
 * backend fails HERE rather than resolving to `undefined` at runtime.
 */
export type AudienceRecord = Pick<
  AudienceDto,
  | 'id'
  | 'title'
  | 'status'
  | 'createdBy'
  | 'definition'
  | 'definitionHash'
  | 'warehouseId'
  | 'lastCountMembers'
  | 'lastCountUniverse'
  | 'lastCountedAt'
  | 'lastCountDefinitionHash'
  // The warehouse's refusal from that count (ACT2-797); read only while the count is fresh.
  | 'lastCountMembershipError'
  | 'updatedAt'
  // The mirrored RudderStack `sql-audience` rETL source and its push state. Null on every audience
  // created before the sync feature. A demo (challenge) workspace pushes nothing to RudderStack but
  // DOES write a synthetic local mirror (`demoPushAudience` sets `rsSourceId = audienceId`), so its
  // audiences read as `synced` here, not as no-mirror;
  // `rsPushError` is the only place a user learns the mirror is broken, because a push failure never
  // fails the save. (There is no sync flag any more — ACT2-614 retired `AUDIENCE_RS_SYNC_ENABLED`.)
  | 'rsSourceId'
  | 'rsAccountId'
  | 'rsPushedDefinitionHash'
  // The SQL format the source was last pushed in; below `RS_PUSHED_SQL_SHAPE` the band offers Sync now.
  | 'rsPushedSqlShape'
  | 'rsPushError'
>;

/**
 * List-only projection: the get DTO fields plus `activationCount` from `audience.list` (ACT2-640).
 * Kept off {@link AudienceRecord} because `audience.get` does not carry the count — the detail page
 * uses AudienceActivationsLink instead.
 */
export type AudienceListRecord = AudienceRecord & { activationCount: number } & Partial<
    Pick<AudienceListRow, 'accountRepair'>
  >;

/** One `audience.list` row; `accountRepair` is its needs-repair status (ACT2-1286). */
type AudienceListRow = Awaited<ReturnType<RouterClient<AppRouter>['audience']['list']>>['audiences'][number];

/** members/universe*100, 1 decimal; 0 when the universe is 0 (mirrors exec.ts's roundPercentage). */
function percentageOf(members: number, universe: number): number {
  if (universe === 0) return 0;
  return Math.round((members / universe) * 1000) / 10;
}

/** `draft` unless the row is explicitly `saved` — an unknown status renders as a draft, never a crash. */
export function statusOf(status: string): 'draft' | 'saved' {
  return status === 'saved' ? 'saved' : 'draft';
}

/**
 * The last recorded count as a `CountState`. `not-run` when nothing has ever been counted; otherwise
 * the stored members/universe as a `counted` state. There is no persisted `error` state — a failed
 * count creates no row and stamps nothing — so the row-derived state is only ever `not-run` or
 * `counted`; an `error` state is produced live by a Calculate action, not read from the row.
 */
export function countStateOf(record: AudienceRecord): CountState {
  if (record.lastCountedAt == null || record.lastCountMembers == null || record.lastCountUniverse == null) {
    return { status: 'not-run' };
  }
  return {
    status: 'counted',
    members: record.lastCountMembers,
    universe: record.lastCountUniverse,
    percentage: percentageOf(record.lastCountMembers, record.lastCountUniverse),
  };
}

/** The stored count was produced for a DIFFERENT definition than the one now stored. Only meaningful
 * once a count has run (a never-counted audience is not "stale"). */
export function isStale(record: AudienceRecord): boolean {
  return record.lastCountedAt != null && record.lastCountDefinitionHash !== record.definitionHash;
}

/**
 * The row carries no count for the definition it currently holds, so one has to be run — the condition
 * `onSave` recounts on (ACT2-604).
 *
 * The server's OWN predicate, negated, not a copy of its rule. `isCountFresh` is what `saveAudience`
 * refuses a promotion to `saved` on, so importing it is what keeps this decision and that refusal in step:
 * a tightened definition of freshness reaches both at once. Its module is a zero-import pure predicate —
 * the same profile as every other `@server/*` alias — and it names itself the single source of truth
 * precisely so surfaces stop re-deriving it.
 *
 * Deliberately WIDER than {@link isStale}, and that gap is why both exist. `isStale` answers "is the
 * number on screen misleading", so a never-counted audience is not stale — it shows nothing to be misled
 * by. This answers "does a count need to run", and a never-counted audience is the clearest yes there is.
 */
export function needsCount(record: AudienceRecord): boolean {
  return !isCountFresh(record.lastCountedAt, record.lastCountDefinitionHash, record.definitionHash);
}

/**
 * How the audience's mirrored RudderStack rETL source stands relative to the definition on screen.
 *
 * `none` is the no-mirror shape — no source, no failure — and renders nothing at all, so an audience
 * that predates the sync looks exactly as it did. (A demo workspace is NOT this shape: its synthetic
 * local mirror makes it `synced`, and an edit after that makes it `warning`, like any other.)
 * `error` wins over every other state. An ordinary push failure does not fail the save, so the
 * broken mirror appears here. The error stays visible even when a first push never created a source.
 */
export type RsSyncState =
  | { status: 'none' }
  /** ACT2-1286: the save skipped its push because the warehouse account was removed; Repair pushes. */
  | { status: 'needs-repair' }
  | { status: 'error'; error: string }
  | { status: 'out-of-sync' }
  | { status: 'synced' };

/**
 * `repairEnabled` is the workspace's repair flag. With it off, a needs-repair `rs_push_error` left from
 * before a rollback reads as an ordinary push error (with Retry), since there is no Repair to wait for.
 */
export function rsSyncStateOf(record: AudienceRecord, repairEnabled = true): RsSyncState {
  if (repairEnabled && isNeedsRepairPushError(record.rsPushError)) return { status: 'needs-repair' };
  if (record.rsPushError != null && record.rsPushError !== '') {
    return { status: 'error', error: record.rsPushError };
  }
  if (record.rsSourceId == null) return { status: 'none' };
  // The mirror exists but was pushed for an earlier definition — the next save re-pushes it.
  if (record.rsPushedDefinitionHash !== record.definitionHash) return { status: 'out-of-sync' };
  // Pushed in an older SQL format: RudderStack still runs the older query, and a pre-ACT2-1196 Snowflake
  // source looks for `member_id`, which the query no longer returns, so every sync fails. The page shows
  // no Save while the audience is clean, so this band's "Sync now" is the one-click repair (ACT2-1311).
  if (record.rsPushedSqlShape !== RS_PUSHED_SQL_SHAPE) return { status: 'out-of-sync' };
  return { status: 'synced' };
}

/**
 * The audience's condition count — the number of top-level predicate conjuncts (an `or` group counts
 * as one). Rendered as the list's "N conditions" chip and the detail page's Definition badge, so the
 * two surfaces show the same number.
 */
export function ruleCountOf(definition: AudienceDefinition): number {
  return topLevelConjuncts(definition).length;
}

export function toAudienceListItem(
  record: AudienceListRecord,
  warehouse: { label: string; type: string },
): AudienceListItem {
  return {
    id: record.id,
    title: record.title,
    status: statusOf(record.status),
    ruleCount: ruleCountOf(record.definition),
    warehouseLabel: warehouse.label,
    warehouseType: warehouse.type,
    activationCount: record.activationCount,
    createdBy: record.createdBy,
    rsSourceId: record.rsSourceId ?? null,
    updatedAt: record.updatedAt,
    countState: countStateOf(record),
    stale: isStale(record),
    repairState: record.accountRepair?.state ?? 'ok',
  };
}
